import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import React, { useEffect, useState } from "react";
import TextField from "@material-ui/core/TextField";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Alert from "@material-ui/lab/Alert";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import { Validate, ValidationGroup } from "mui-validate";
import UserDataService from "../../../services/UserService";
import DeviceDataService from "../../../services/DeviceService";
import RoomsDataService from "../../../services/RoomsService";
import DeviceGroupsDataService from "../../../services/DeviceGroupsService";
import Autocomplete from "@material-ui/lab/Autocomplete";
import "date-fns";
import axios from "axios";
import { API_URL } from "../../../utils/setApiUrl";
import {
    Checkbox,
    FormControlLabel,
    FormGroup,
    useMediaQuery,
    Tooltip,
    IconButton,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useStyles } from "../../styles/useStyles";
import { styled, ThemeProvider } from "@mui/system";
import { createTheme } from "@mui/material/styles";
import IssuedCardsService from "../../../services/IssuedCardsService";
import dayjs from "dayjs";
import {
    LocalizationProvider,
    MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Grid } from "@material-ui/core";
import DeviceFloorService from "../../../services/DeviceFloorService";
import DeviceUsersService from "../../../services/DeviceUsersService";

const theme = createTheme(); // Create a theme instance

require("dotenv").config();

const CustomMobileDateTimePicker = styled(MobileDateTimePicker)(
    ({ theme }) => ({
        "& .MuiInputBase-input.MuiOutlinedInput-input": {
            padding: "8.5px 14px",
        },
    })
);

// Style for consistent card height
const StyledCard = styled(Card)({
    height: "100%",
    display: "flex",
    flexDirection: "column",
});

const StyledCardContent = styled(CardContent)({
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
});

function OfflineCardWriterForm() {
    const classes = useStyles();
    const [loc] = React.useState(() => {
        const locData = JSON.parse(localStorage.getItem("loc"));
        // Handle both formats: {"id": "all"} or {"id": [1]} or just [1]
        if (locData.id !== undefined) {
            return locData.id;
        }
        return locData; // Direct array like [1] or [1,2]
    });
    const [userLevel] = React.useState(localStorage.getItem("level"));
    const isSpaLevel = userLevel === "spa";

    // * Users Dropdown Start
    const [users, setUsers] = useState([]);

    useEffect(() => {
        retrieveUsers();
    }, []);

    const retrieveUsers = () => {
        UserDataService.adminAll()
            .then((response) => {
                let filteredUsers = response.data.filter(
                    (user) => user.sub_loc_name === "Гости"
                );
                // For non-admin users, also filter by location
                if (loc !== "all") {
                    const locIds = (Array.isArray(loc) ? loc : [loc]).map(Number);
                    filteredUsers = filteredUsers.filter(
                        (user) => locIds.includes(Number(user.loc_number))
                    );
                }
                // Sort by sap descending (latest users first)
                filteredUsers.sort((a, b) => b.sap - a.sap);
                setUsers(filteredUsers);
            })
            .catch((e) => {
                setErrorMessage(["Cannot load user data"]);
                setIserror(true);
            });
    };

    const [userValue, setUserValue] = React.useState(null);
    const [userInputValue, setUserInputValue] = React.useState("");

    // Auto-prefill room and end date when user is selected
    useEffect(() => {
        if (userValue && userValue.guest_id && userValue.end_date && userValue.room_name) {
            // Check if end_date is in the future
            const userEndDate = dayjs(userValue.end_date);
            const currentDate = dayjs();

            if (userEndDate.isAfter(currentDate)) {
                // Find matching room in devices array by room_name
                const matchingRoom = devices.find(
                    (device) => device.room_number === parseInt(userValue.room_name) ||
                        device.device_name === userValue.room_name
                );

                // Set the room if found
                if (matchingRoom) {
                    setDeviceValue(matchingRoom);
                    setDeviceInputValue(matchingRoom.device_name);
                } else {
                    // Clear room if no match found
                    setDeviceValue(null);
                    setDeviceInputValue("");
                }

                // Set the end date
                setSelectedDateTime(userEndDate);
            } else {
                // Clear fields if end_date is not in the future
                setDeviceValue(null);
                setDeviceInputValue("");
                setSelectedDateTime(dayjs());
            }
        } else {
            // Clear fields if user doesn't have required data
            setDeviceValue(null);
            setDeviceInputValue("");
            setSelectedDateTime(dayjs());
        }
    }, [userValue, devices]);

    // * Devices Dropdown Start
    const [devices, setDevices] = useState([]);
    const [writers, setWriters] = useState([]);
    const [groups, setGroups] = useState([]);

    useEffect(() => {
        retrieveWriters();
        retrieveDevices();
        retrieveGroups();
    }, []);

    const retrieveWriters = () => {
        if (loc === "all") {
            DeviceDataService.adminAllWriters()
                .then((response) => {
                    setWriters(response.data);
                })
                .catch((e) => {
                    setErrorMessage(["Cannot load device data"]);
                    setIserror(true);
                });
        } else {
            const locIds = Array.isArray(loc) ? loc : [loc];
            Promise.all(locIds.map((locId) => DeviceDataService.getAllWriters(locId)))
                .then((responses) => {
                    const allWriters = responses.flatMap((response) => response.data);
                    setWriters(allWriters);
                })
                .catch((e) => {
                    setErrorMessage(["Cannot load device data"]);
                    setIserror(true);
                });
        }
    };

    const retrieveDevices = () => {
        RoomsDataService.getAll()
            .then((response) => {
                if (loc === "all") {
                    setDevices(response.data);
                } else {
                    const locIds = (Array.isArray(loc) ? loc : [loc]).map(Number);
                    const filteredDevices = response.data.filter(
                        (device) => locIds.includes(Number(device.loc))
                    );
                    setDevices(filteredDevices);
                }
            })
            .catch((e) => {
                setErrorMessage(["Cannot load device data"]);
                setIserror(true);
            });
    };

    const retrieveGroups = () => {
        const userLevel = localStorage.getItem("level");
        const isReceptionOrSpa = userLevel === "reception" || userLevel === "spa";

        const fetchGroups = loc === "all"
            ? DeviceGroupsDataService.getAllAdmin().then((res) => res.data)
            : Promise.all(
                (Array.isArray(loc) ? loc : [loc]).map((locId) => DeviceGroupsDataService.getAll(locId))
            ).then((responses) => responses.flatMap((res) => res.data));

        fetchGroups
            .then((data) => {
                let filteredGroups = data;
                if (isReceptionOrSpa) {
                    filteredGroups = filteredGroups.filter(
                        (group) => !group.name.includes("Мастър")
                    );
                }

                // Add a default "No Group" option with id 0
                const groupsWithDefault = [
                    { id: 0, name: "Без група" },
                    ...filteredGroups,
                ];
                setGroups(groupsWithDefault);
                // Set default group value
                setGroupValue(groupsWithDefault[0]);
            })
            .catch((e) => {
                setErrorMessage(["Cannot load group data"]);
                setIserror(true);
            });
    };

    const [duplicate, setDuplicate] = React.useState(false);

    const handleDuplicate = (event) => {
        setDuplicate(event.target.checked);
    };

    const [deviceValue, setDeviceValue] = React.useState(null);
    const [deviceInputValue, setDeviceInputValue] = React.useState("");

    const [writerValue, setWriterValue] = React.useState(null);
    const [writerInputValue, setWriterInputValue] = React.useState("");

    // Initialize with null - will be set when groups are loaded
    const [groupValue, setGroupValue] = React.useState(null);
    const [groupInputValue, setGroupInputValue] = React.useState("");

    // * for error handling
    const [iserror, setIserror] = useState(false);
    const [successMessage, setSuccessMessage] = useState([]);
    const [errorMessage, setErrorMessage] = useState([]);

    // * Dates handling

    const [selectedEndDate, setSelectedEndDate] = useState();

    const handleEndDateChange = (date) => {
        const newDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            15
        );
        setSelectedEndDate(newDate);
    };

    // * SelectedStartDate
    const [selectedStartDate, handleStartDateChange] = useState(
        dayjs()
            .hour(14)
            .minute(0)
            .second(0)
    );

    const formattedStartDate = selectedStartDate.format("YYYY-MM-DD HH:mm");

    // * SelectedEndDateTime
    const [selectedDateTime, setSelectedDateTime] = useState(dayjs());

    const handleDateTimeChange = (date) => {
        const bulgarianTime = date;
        setSelectedDateTime(bulgarianTime);
    };

    const formattedEndDate = selectedDateTime.format("YYYY-MM-DD HH:mm");
    const formattedEndHours = selectedDateTime.format("HH");
    const formattedEndMinutes = selectedDateTime.format("mm");

    const [events, setWriter] = useState([]);

    function showErrorAlert(message) {
        setSuccessMessage(false);
        setErrorMessage(
            <Alert severity="error" style={{ marginBottom: "25px" }}>
                {message}
            </Alert>
        );
        setTimeout(() => {
            setErrorMessage(false);
        }, 5000);
    }

    function showSuccessAlert(message) {
        setErrorMessage(false);
        setSuccessMessage(
            <Alert severity="success" style={{ marginBottom: "25px" }}>
                {message}
            </Alert>
        );
        setTimeout(() => {
            setSuccessMessage(false);
        }, 5000);
    }

    function getRandomEightDigitNumber() {
        let number = "";
        for (let i = 0; i < 8; i++) {
            number += Math.floor(Math.random() * 10); // Generate a random digit between 0 and 9
        }
        return parseInt(number, 10);
    }

    // Check if the current host is localhost:4012 or samelyon.biodit.bg
    const currentHost = window.location.host;
    const showGroupSelection =
        currentHost === "samelyon.biodit.bg" || currentHost === "localhost:4012" || /^10\.0\./.test(currentHost);

    // Function to check for and control waterleak device
    const controlWaterleakDevice = async (roomName) => {
        if (!roomName) return;

        try {
            console.log(`Checking for waterleak device for room: ${roomName}`);

            // Get all waterleak devices
            const waterleakDevicesResponse = await DeviceDataService.adminAllWaterleakDevices();
            const waterleakDevices = waterleakDevicesResponse.data;

            // Find a device that matches the pattern "{roomName} waterleak"
            const waterleakDeviceName = `${roomName} waterleak`;
            const waterleakDevice = waterleakDevices.find(
                (device) =>
                    device.name === waterleakDeviceName && device.online === true
            );

            if (waterleakDevice && waterleakDevice.mac) {
                console.log(
                    `Found waterleak device for room ${roomName}: ${waterleakDevice.name} (MAC: ${waterleakDevice.mac})`
                );

                // Send the heater command to the waterleak device
                const heaterResponse = await axios.post(`${API_URL}/setHeaterOn`, {
                    mac: waterleakDevice.mac,
                });

                console.log(`Heater command response:`, heaterResponse.data);
            } else {
                console.log(`No matching waterleak device found for room: ${roomName}`);
            }
        } catch (error) {
            console.error(
                `Error controlling waterleak device for room ${roomName}:`,
                error
            );
            // Don't fail the card issuance if waterleak control fails
        }
    };

    // Function to calculate floors bitmask
    const calculateFloorsBitmask = (floors) => {
        console.log(`Calculating bitmask for floors: [${floors.join(", ")}]`);
        let bitmask = 0;

        // Floor to relay mapping according to the formula (8 relays total):
        // Floor -1 → Relay 1 → Bit 0 → 2^0 = 1
        // Floor 0 → Relay 2 → Bit 1 → 2^1 = 2
        // Floor 1 → Relay 3 → Bit 2 → 2^2 = 4
        // Floor 2 → Relay 4 → Bit 3 → 2^3 = 8
        // Floor 3 → Relay 5 → Bit 4 → 2^4 = 16
        // Floor 4 → Relay 6 → Bit 5 → 2^5 = 32
        // Floor 5 → Relay 7 → Bit 6 → 2^6 = 64
        // Floor 6 → Relay 8 → Bit 7 → 2^7 = 128

        floors.forEach((floor) => {
            // Convert floor to number if it's a string
            const floorNumber = typeof floor === 'string' ? parseInt(floor) : floor;

            // Calculate bit position: Floor F → Bit position (F + 1)
            const bitPosition = floorNumber + 1;

            // Check if bit position is valid (0-7 for 8 relays)
            if (bitPosition >= 0 && bitPosition <= 7) {
                const bitValue = Math.pow(2, bitPosition);
                const relayNumber = bitPosition + 1;
                console.log(
                    `Floor ${floorNumber} → Relay ${relayNumber} → Bit position ${bitPosition} → Value ${bitValue}`
                );
                bitmask += bitValue;
            } else {
                console.warn(`Floor ${floorNumber} is out of range. Valid range is -1 to 6.`);
            }
        });

        console.log(
            `Final bitmask: ${bitmask} (binary: ${bitmask
                .toString(2)
                .padStart(8, "0")})`
        );
        return bitmask;
    };

    // Function to add elevator access for the user
    const addElevatorAccess = async (userId, userCard, userQrCode, roomFloor) => {
        const client = axios.create({
            baseURL: API_URL,
        });

        try {
            console.log(
                `Adding elevator access for user ${userId} to room floor ${roomFloor}`
            );
            console.log(`User card: ${userCard}, User QR code: ${userQrCode}`);

            // Get all elevator devices
            const elevatorDevicesResponse = await DeviceDataService.adminAllElevatorDevices();
            const elevators = elevatorDevicesResponse.data;

            // Calculate floors bitmask (always include floors -1, 0, and room floor)
            const accessFloors = [-1, 0]; // Always include basement and ground floor

            // Add room floor if it's not already included and is valid
            console.log(`Room floor value: ${roomFloor}, type: ${typeof roomFloor}`);

            if (
                roomFloor !== undefined &&
                roomFloor !== null &&
                roomFloor !== -1 &&
                roomFloor !== 0
            ) {
                // Convert to number if it's a string
                const floorNumber =
                    typeof roomFloor === "string" ? parseInt(roomFloor) : roomFloor;
                console.log(`Converted room floor: ${floorNumber}`);

                // Valid floor range is -1 to 6 (for 8 relays)
                if (floorNumber >= -1 && floorNumber <= 6) {
                    accessFloors.push(floorNumber);
                    console.log(`Added room floor ${floorNumber} to access floors`);
                } else {
                    console.warn(`Room floor ${floorNumber} is not in valid range (-1 to 6)`);
                }
            } else {
                console.log(`Room floor is ${roomFloor}, not adding to access floors`);
            }

            console.log(`Final access floors array: [${accessFloors.join(", ")}]`);

            const floorsBitmask = calculateFloorsBitmask(accessFloors);

            console.log(
                `User will have access to floors: ${accessFloors.join(
                    ", "
                )} (bitmask: ${floorsBitmask})`
            );
            console.log(
                `Binary representation: ${floorsBitmask.toString(2).padStart(8, "0")}`
            );

            // For each elevator device
            for (const elevator of elevators) {
                try {
                    console.log(
                        `Processing elevator: ${elevator.name} (MAC: ${elevator.mac})`
                    );

                    // Get free slot for the elevator
                    const elevatorSlotResponse = await client.post("/freeSlot", {
                        device: elevator.id,
                    });
                    const elevatorSlot = elevatorSlotResponse.data.message;

                    console.log(`Got slot ${elevatorSlot} for elevator ${elevator.name}`);

                    // Create device user record for elevator
                    await DeviceUsersService.create({
                        device_id: elevator.id,
                        user_id: userId,
                        slot: elevatorSlot,
                        template_id: null,
                    });

                    console.log(
                        `Created device user record for elevator ${elevator.name}`
                    );

                    // Send MQTT insert command for elevator with floors bitmask
                    const insertData = {
                        device: elevator.mac,
                        slot: elevatorSlot,
                        user: userId,
                        pin: null,
                        template: null,
                        card: userCard,
                        qr_code: userQrCode,
                        floor: floorsBitmask, // Use calculated bitmask for floor access
                    };

                    console.log(`Sending MQTT insert for ${elevator.name}:`, insertData);
                    console.log(
                        `Floor bitmask ${floorsBitmask} should activate relays for floors: ${accessFloors.join(
                            ", "
                        )}`
                    );
                    console.log(
                        `Relay mapping: Floor 3=Relay1, Floor 2=Relay2, Floor 1=Relay3, Floor -1=Relay4, Floor 0=Relay5`
                    );

                    await client.post("/insert", insertData);

                    console.log(
                        `Sent MQTT insert command for elevator ${elevator.name} with floors bitmask ${floorsBitmask}`
                    );
                } catch (elevatorError) {
                    console.error(
                        `Error processing elevator ${elevator.name}:`,
                        elevatorError
                    );
                    // Continue with other elevators even if one fails
                }
            }
        } catch (error) {
            console.error("Error adding elevator access:", error);
            // Don't fail the card issuance if elevator access fails
        }
    };

    // Fitness device MAC address - hardcoded for this specific deployment
    const FITNESS_DEVICE_MAC = "w028108d8c100";

    // Function to add fitness access for the user
    const addFitnessAccess = async (userId, userCard, userQrCode) => {
        const client = axios.create({
            baseURL: API_URL,
        });

        try {
            console.log(`Adding fitness access for user ${userId}`);
            console.log(`User card: ${userCard}, User QR code: ${userQrCode}`);

            // Get the fitness device by MAC address
            const fitnessDeviceResponse = await DeviceDataService.getByMac(FITNESS_DEVICE_MAC);
            const fitnessDevice = fitnessDeviceResponse.data;

            if (!fitnessDevice || !fitnessDevice.id) {
                console.warn(`Fitness device with MAC ${FITNESS_DEVICE_MAC} not found`);
                return;
            }

            console.log(`Found fitness device: ${fitnessDevice.name} (MAC: ${fitnessDevice.mac})`);

            // Get free slot for the fitness device
            const fitnessSlotResponse = await client.post("/freeSlot", {
                device: fitnessDevice.id,
            });
            const fitnessSlot = fitnessSlotResponse.data.message;

            console.log(`Got slot ${fitnessSlot} for fitness device ${fitnessDevice.name}`);

            // Create device user record for fitness device
            await DeviceUsersService.create({
                device_id: fitnessDevice.id,
                user_id: userId,
                slot: fitnessSlot,
                template_id: null,
            });

            console.log(`Created device user record for fitness device ${fitnessDevice.name}`);

            // Send MQTT insert command for fitness device
            const insertData = {
                device: fitnessDevice.mac,
                slot: fitnessSlot,
                user: userId,
                pin: null,
                template: null,
                card: userCard,
                qr_code: userQrCode
            };

            console.log(`Sending MQTT insert for fitness device:`, insertData);

            await client.post("/insert", insertData);

            console.log(`Sent MQTT insert command for fitness device ${fitnessDevice.name}`);
        } catch (error) {
            console.error("Error adding fitness access:", error);
            // Don't fail the card issuance if fitness access fails
        }
    };

    const saveCard = (date) => {
        const chosenTime = dayjs(date);
        const chosenHour = chosenTime.hour();
        const chosenMinutes = chosenTime.minute();

        const client = axios.create({
            baseURL: API_URL,
        });

        if (duplicate === false) {
            async function writeCard() {
                try {
                    let guestCounter = 0;
                    let employeeCounter = 0;
                    let masterCounter = 0;

                    if (!isSpaLevel) {
                        try {
                            const [guestCounterResponse, roomResponse] = await Promise.all([
                                client.post(
                                    `/v1/rooms/guest/counter/${deviceValue.room_number}`,
                                    null,
                                    {
                                        validateStatus: () => true,
                                    }
                                ),
                                new Promise((resolve) =>
                                    setTimeout(() => {
                                        client
                                            .get(`/v1/rooms/${deviceValue.room_number}`, {
                                                validateStatus: () => true,
                                            })
                                            .then(resolve);
                                    }, 1500)
                                ),
                            ]);

                            if (
                                roomResponse.status !== 200 ||
                                guestCounterResponse.status !== 200
                            ) {
                                showErrorAlert(
                                    "Картата не беше издадена. Моля опитайте отново!"
                                );
                                return;
                            }

                            const roomData = roomResponse.data[0];
                            guestCounter = roomData.guest_counter;
                            employeeCounter = roomData.employee_counter;
                            masterCounter = roomData.master_counter;
                        } catch (error) {
                            showErrorAlert(
                                "Грешка при четене на данни за стаята. Моля опитайте отново!"
                            );
                            return;
                        }
                    }

                    // Prepare the request data for card issuance
                    const cardRequestData = {
                        command: "write",
                        duplicate: false,
                        card_number: getRandomEightDigitNumber(),
                        device: writerValue.mac,
                        hotel: parseInt(process.env.REACT_APP_HOTEL_NUMBER),
                        room: isSpaLevel
                            ? userValue && userValue.room_name
                                ? parseInt(userValue.room_name)
                                : 0
                            : deviceValue.room_number,
                        group: showGroupSelection && groupValue ? groupValue.id : 0,
                        user_id: userValue ? parseInt(userValue.sap) : parseInt("1234"),
                        user_type: 0,
                        end_date: JSON.stringify(selectedDateTime)
                            .replace(/\"/g, "")
                            .split(".")[0],
                        end_hour: formattedEndHours,
                        end_minutes: formattedEndMinutes,
                        end_time: formattedEndDate,
                        start_time: JSON.stringify(selectedStartDate)
                            .replace("T", " ")
                            .replace(/.{0,9}$/, '"')
                            .replace(/\"/g, ""),
                        admin_id: 1,
                        guest_count: guestCounter,
                        staff_count: employeeCounter,
                        master_count: masterCounter,
                    };

                    // Prepare the data for issuedCards record with room_name for heater control
                    const issuedCardData = {
                        ...cardRequestData,
                        // Include the room_name for heater control
                        room_name: isSpaLevel
                            ? userValue && userValue.room_name
                                ? userValue.room_name
                                : null
                            : deviceValue
                                ? deviceValue.device_name
                                : null,
                        heater_status: "ON", // Add heater status to track whether we need to turn it off
                    };

                    try {
                        // Create the issued card record with room_name
                        const issuedCard = await IssuedCardsService.create(issuedCardData);
                        console.log("Issued Card created:", issuedCard);

                        // Update the user with the new room data if user is selected
                        if (userValue) {
                            const userData = {
                                end_date: formattedEndDate,
                            };

                            // Only update room_name if not SPA level
                            if (!isSpaLevel && deviceValue && deviceValue.room_number) {
                                userData.room_name = deviceValue.room_number;
                            }

                            try {
                                await UserDataService.update(userValue.sap, userData);
                                console.log("User updated with new room data:", userData);
                            } catch (updateError) {
                                console.error("Error updating user data:", updateError);
                            }
                        }

                        // Send the card writer request (without room_name)
                        const { data: response } = await client.post(
                            "/writeCard",
                            cardRequestData,
                            {
                                timeout: 20000,
                            }
                        );

                        setWriter(response.data);
                        console.log("Full response:", response);
                        console.log("Response data:", response.data);
                        console.log("Response data type:", typeof response.data);
                        showSuccessAlert("Картата беше успешно издадена!");

                        // Extract the actual card number from the response
                        // Response format: 58bf2589cc74/OK/334EDB11/0856286645
                        let actualCardNumber = null;
                        let responseString = null;

                        // Try different ways to get the response string
                        if (typeof response.data === "string") {
                            responseString = response.data;
                        } else if (typeof response === "string") {
                            responseString = response;
                        } else if (
                            response.data &&
                            typeof response.data.data === "string"
                        ) {
                            responseString = response.data.data;
                        }

                        console.log("Response string for parsing:", responseString);

                        if (responseString) {
                            const parts = responseString.split("/");
                            console.log("Split parts:", parts);
                            if (parts.length >= 4) {
                                actualCardNumber = parts[parts.length - 1]; // Get the last part
                                console.log("Extracted card number:", actualCardNumber);
                            }
                        }

                        if (!actualCardNumber) {
                            console.warn("Could not extract card number from response");
                        }

                        // After successful card issuance, control waterleak device
                        if (!isSpaLevel) {
                            controlWaterleakDevice(deviceValue.device_name);
                        }

                        // Add elevator access for the user
                        if (!isSpaLevel && userValue && deviceValue) {
                            try {
                                // Get the floor information for the selected room
                                const floorInfo = await DeviceFloorService.getFloorsForDevice(
                                    deviceValue.id
                                );
                                const roomFloor =
                                    floorInfo.data[0] && floorInfo.data[0].Floor
                                        ? floorInfo.data[0].Floor.level
                                        : 0;

                                await addElevatorAccess(
                                    userValue.sap,
                                    actualCardNumber,
                                    userValue.guest_id || null,
                                    roomFloor
                                );
                            } catch (floorError) {
                                console.error(
                                    "Error getting floor info or adding elevator access:",
                                    floorError
                                );
                            }
                        }

                        // Add fitness access for the user (all guests) - only for iztokplaza.biodit.bg
                        if (userValue && window.location.host === "iztokplaza.biodit.bg") {
                            try {
                                await addFitnessAccess(
                                    userValue.sap,
                                    actualCardNumber,
                                    userValue.guest_id || null
                                );
                            } catch (fitnessError) {
                                console.error(
                                    "Error adding fitness access:",
                                    fitnessError
                                );
                            }
                        }
                    } catch (error) {
                        console.error("Error creating issued card or writing card:", error);
                        showErrorAlert(
                            "Грешка при издаване на картата. Моля опитайте отново!"
                        );
                    }
                } catch (error) {
                    if (error.response && error.response.data) {
                        const parts = error.response.data.split("/", 3); // ["3494542c4ca0", "NOK", "No card found!"]
                        const remainingMessage = parts.slice(2).join("/"); // "No card found!"

                        if (remainingMessage === "No card found!") {
                            showErrorAlert(
                                "Липсва карта. Моля поставете нова и опитайте отново!"
                            );
                        } else if (remainingMessage === "Can't authenticate!") {
                            showErrorAlert(
                                "Невалидна карта. Моля поставете нова и опитайте отново!"
                            );
                        }
                    } else {
                        showErrorAlert("Устройството не отговаря. Моля опитайте отново!");
                    }
                    setSuccessMessage(false);
                }
            }

            writeCard();
        } else if (duplicate === true) {
            async function handleWriteCard() {
                try {
                    let guestCounter = 0;
                    let employeeCounter = 0;
                    let masterCounter = 0;

                    if (!isSpaLevel) {
                        try {
                            const roomId = parseInt(deviceValue.room_number);
                            const { data, status } = await client.get(`/v1/rooms/${roomId}`);

                            if (status !== 200 || !data || !data.length) {
                                showErrorAlert(
                                    "Грешка при четене на данни за стаята. Моля опитайте отново!"
                                );
                                return;
                            }

                            const roomData = data[0]; // Get the first object in the array
                            guestCounter = roomData.guest_counter;
                            employeeCounter = roomData.employee_counter;
                            masterCounter = roomData.master_counter;
                        } catch (error) {
                            showErrorAlert(
                                "Грешка при четене на данни за стаята. Моля опитайте отново!"
                            );
                            return;
                        }
                    }

                    // Prepare the request data for card issuance
                    const cardRequestData = {
                        command: "write",
                        duplicate: true,
                        card_number: getRandomEightDigitNumber(),
                        device: writerValue.mac,
                        hotel: parseInt(process.env.REACT_APP_HOTEL_NUMBER),
                        room: isSpaLevel
                            ? userValue && userValue.room_name
                                ? parseInt(userValue.room_name)
                                : 0
                            : deviceValue.room_number,
                        group: showGroupSelection && groupValue ? groupValue.id : 0,
                        user_id: userValue ? parseInt(userValue.sap) : parseInt("1234"),
                        user_type: 0,
                        end_date: JSON.stringify(selectedDateTime)
                            .replace(/\"/g, "")
                            .split(".")[0],
                        end_hour: formattedEndHours,
                        end_minutes: formattedEndMinutes,
                        end_time: formattedEndDate,
                        start_time: JSON.stringify(selectedStartDate)
                            .replace("T", " ")
                            .replace(/.{0,9}$/, '"')
                            .replace(/\"/g, ""),
                        admin_id: 1,
                        guest_count: guestCounter,
                        staff_count: employeeCounter,
                        master_count: masterCounter,
                    };

                    // Prepare the data for issuedCards record with room_name for heater control
                    const issuedCardData = {
                        ...cardRequestData,
                        // Include the room_name for heater control
                        room_name: isSpaLevel
                            ? userValue && userValue.room_name
                                ? userValue.room_name
                                : null
                            : deviceValue
                                ? deviceValue.device_name
                                : null,
                        heater_status: "ON", // Add heater status to track whether we need to turn it off
                    };

                    try {
                        // Create the issued card record with room_name
                        const issuedCard = await IssuedCardsService.create(issuedCardData);
                        console.log("Issued Card created:", issuedCard);

                        // Update the user with the new room data if user is selected
                        if (userValue) {
                            const userData = {
                                end_date: formattedEndDate,
                            };

                            // Only update room_name if not SPA level
                            if (!isSpaLevel && deviceValue && deviceValue.room_number) {
                                userData.room_name = deviceValue.room_number;
                            }

                            try {
                                await UserDataService.update(userValue.sap, userData);
                                console.log("User updated with new room data:", userData);
                            } catch (updateError) {
                                console.error("Error updating user data:", updateError);
                            }
                        }

                        // Send the card writer request (without room_name)
                        const { data: response } = await client.post(
                            "/writeCard",
                            cardRequestData,
                            {
                                timeout: 20000,
                            }
                        );

                        setWriter(response.data);
                        console.log("Full response:", response);
                        console.log("Response data:", response.data);
                        console.log("Response data type:", typeof response.data);
                        showSuccessAlert("Картата беше успешно издадена!");

                        // Extract the actual card number from the response
                        // Response format: 58bf2589cc74/OK/334EDB11/0856286645
                        let actualCardNumber = null;
                        let responseString = null;

                        // Try different ways to get the response string
                        if (typeof response.data === "string") {
                            responseString = response.data;
                        } else if (typeof response === "string") {
                            responseString = response;
                        } else if (
                            response.data &&
                            typeof response.data.data === "string"
                        ) {
                            responseString = response.data.data;
                        }

                        console.log("Response string for parsing:", responseString);

                        if (responseString) {
                            const parts = responseString.split("/");
                            console.log("Split parts:", parts);
                            if (parts.length >= 4) {
                                actualCardNumber = parts[parts.length - 1]; // Get the last part
                                console.log("Extracted card number:", actualCardNumber);
                            }
                        }

                        if (!actualCardNumber) {
                            console.warn("Could not extract card number from response");
                        }

                        // After successful card issuance, control waterleak device
                        if (!isSpaLevel) {
                            controlWaterleakDevice(deviceValue.device_name);
                        }

                        // Add elevator access for the user
                        if (!isSpaLevel && userValue && deviceValue) {
                            try {
                                // Get the floor information for the selected room
                                const floorInfo = await DeviceFloorService.getFloorsForDevice(
                                    deviceValue.id
                                );
                                const roomFloor =
                                    floorInfo.data[0] && floorInfo.data[0].Floor
                                        ? floorInfo.data[0].Floor.level
                                        : 0;

                                await addElevatorAccess(
                                    userValue.sap,
                                    actualCardNumber,
                                    userValue.guest_id || null,
                                    roomFloor
                                );
                            } catch (floorError) {
                                console.error(
                                    "Error getting floor info or adding elevator access:",
                                    floorError
                                );
                            }
                        }

                        // Add fitness access for the user (all guests) - only for iztokplaza.biodit.bg
                        if (userValue && window.location.host === "iztokplaza.biodit.bg") {
                            try {
                                await addFitnessAccess(
                                    userValue.sap,
                                    actualCardNumber,
                                    userValue.guest_id || null
                                );
                            } catch (fitnessError) {
                                console.error(
                                    "Error adding fitness access:",
                                    fitnessError
                                );
                            }
                        }
                    } catch (error) {
                        console.error("Error creating issued card or writing card:", error);
                        showErrorAlert(
                            "Грешка при издаване на картата. Моля опитайте отново!"
                        );
                    }
                } catch (error) {
                    console.log(error);
                    if (error.response && error.response.data) {
                        const parts = error.response.data.split("/", 3); // ["3494542c4ca0", "NOK", "No card found!"]
                        const remainingMessage = parts.slice(2).join("/"); // "No card found!"

                        if (remainingMessage === "No card found!") {
                            showErrorAlert(
                                "Липсва карта. Моля поставете нова и опитайте отново!"
                            );
                        } else if (remainingMessage === "Can't authenticate!") {
                            showErrorAlert(
                                "Невалидна карта. Моля поставете нова и опитайте отново!"
                            );
                        }
                    } else {
                        showErrorAlert("Устройството не отговаря. Моля опитайте отново!");
                    }
                    setSuccessMessage(false);
                }
            }

            handleWriteCard();
        }
    };

    const isSmallScreen = useMediaQuery("(max-width:600px)");

    return (
        <ThemeProvider theme={theme}>
            <StyledCard className={classes.cardWriterBox}>
                <AppBar position="static" elevation={0} color="primary">
                    <Toolbar className="px-8">
                        <Typography
                            variant="subtitle1"
                            color="inherit"
                            className="flex-1 px-12"
                            style={{ display: 'flex', alignItems: 'center' }}
                        >
                            2. Издаване на карта за Гост
                            <Tooltip
                                title={
                                    <span style={{ whiteSpace: 'pre-line' }}>
                                        За гости от Clock PMS автоматично се попълват полетата за стая и крайна дата.
                                        {'\n\n'}
                                        За ръчно добавени гости трябва Вие да изберете стая и дата ръчно.
                                    </span>
                                }
                                arrow
                            >
                                <IconButton size="small" style={{ color: 'white', marginLeft: '8px' }}>
                                    <HelpOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Typography>
                    </Toolbar>
                </AppBar>
                <StyledCardContent>
                    <div className="submit-form">
                        {successMessage}
                        {errorMessage}
                        <ValidationGroup>
                            <form className={classes.cardWriterBoxRoot}>
                                <Grid container spacing={1}>
                                    <Grid item xs={isSmallScreen ? 12 : 6}>
                                        <Validate name="user">
                                            <Autocomplete
                                                value={userValue}
                                                onChange={(event, newUserValue) => {
                                                    setUserValue(newUserValue);
                                                }}
                                                inputValue={userInputValue}
                                                onInputChange={(event, newInputValue) => {
                                                    setUserInputValue(newInputValue);
                                                }}
                                                id="auto-select-user"
                                                getOptionLabel={(user) =>
                                                    `${user.name} (${user.guest_id ? 'От Clock PMS' : 'Ръчно добавен'})`
                                                }
                                                className={classes.textField}
                                                options={users}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Гост"
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                    />
                                                )}
                                            />
                                        </Validate>
                                    </Grid>

                                    {!isSpaLevel && (
                                        <Grid item xs={isSmallScreen ? 12 : 6}>
                                            <Validate name="room">
                                                <Autocomplete
                                                    value={deviceValue}
                                                    onChange={(event, newDeviceValue) => {
                                                        setDeviceValue(newDeviceValue);
                                                    }}
                                                    inputValue={deviceInputValue}
                                                    onInputChange={(event, deviceInputValue) => {
                                                        setDeviceInputValue(deviceInputValue);
                                                    }}
                                                    id="auto-select device"
                                                    getOptionLabel={(devices) => devices.device_name}
                                                    className={classes.textField}
                                                    options={devices}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Стая"
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                        />
                                                    )}
                                                />
                                            </Validate>
                                        </Grid>
                                    )}

                                    <Grid item xs={isSmallScreen ? 12 : 6}>
                                        <Validate name="writer">
                                            <Autocomplete
                                                value={writerValue}
                                                onChange={(event, newWriterValue) => {
                                                    setWriterValue(newWriterValue);
                                                }}
                                                inputValue={writerInputValue}
                                                onInputChange={(event, writerInputValue) => {
                                                    setWriterInputValue(writerInputValue);
                                                }}
                                                id="auto-select writer"
                                                getOptionLabel={(writers) => writers.name}
                                                className={classes.textField}
                                                options={writers}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Записващо Устройство"
                                                        variant="outlined"
                                                        size="small"
                                                        fullWidth
                                                    />
                                                )}
                                            />
                                        </Validate>
                                    </Grid>

                                    {showGroupSelection && (
                                        <Grid item xs={isSmallScreen ? 12 : 6}>
                                            <Validate
                                                name="group"
                                                required={[false, "This field is required!"]}
                                            >
                                                <Autocomplete
                                                    value={groupValue}
                                                    onChange={(event, newGroupValue) => {
                                                        setGroupValue(newGroupValue);
                                                    }}
                                                    inputValue={groupInputValue}
                                                    onInputChange={(event, groupInputValue) => {
                                                        setGroupInputValue(groupInputValue);
                                                    }}
                                                    id="auto-select group"
                                                    getOptionLabel={(groups) => groups.name}
                                                    className={classes.textField}
                                                    options={groups}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Група"
                                                            variant="outlined"
                                                            size="small"
                                                            fullWidth
                                                        />
                                                    )}
                                                />
                                            </Validate>
                                        </Grid>
                                    )}

                                    <Grid item xs={isSmallScreen ? 12 : 6}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <CustomMobileDateTimePicker
                                                label="Крайна Дата"
                                                defaultValue={dayjs()}
                                                ampm={false}
                                                value={selectedDateTime}
                                                onChange={handleDateTimeChange}
                                                fullWidth
                                            />
                                        </LocalizationProvider>
                                    </Grid>

                                    <Grid item xs={isSmallScreen ? 12 : 6}>
                                        <FormGroup
                                            style={{
                                                marginLeft: "10px",
                                                display: "inline-block",
                                                paddingTop: "0px",
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={duplicate}
                                                        onChange={handleDuplicate}
                                                        inputProps={{ "aria-label": "controlled" }}
                                                    />
                                                }
                                                label="Дубликат"
                                            />
                                        </FormGroup>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Button
                                            onClick={saveCard}
                                            variant="contained"
                                            color="primary"
                                            fullWidth={isSmallScreen}
                                            disabled={!userValue || (!isSpaLevel && !deviceValue) || !writerValue}
                                        >
                                            Издаване на карта
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </ValidationGroup>
                    </div>
                </StyledCardContent>
            </StyledCard>
        </ThemeProvider>
    );
}

export default OfflineCardWriterForm;
